import { useEffect, useState } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";

// Phase 24 (C2) — admin editor for the per-SURFACE scope mode (which cases'
// data appears on each surface), persisted to FirmSettings.surfaceScopes via
// PUT /api/firm-settings/surface-scopes (admin-gated). Any surface may be set
// to any mode — prohibition stays dynamically possible.

const SURFACES = [
  { key: "caseView", label: "Case view (list & detail)" },
  { key: "collectiveCaseView", label: "Collective-case view" },
  { key: "dashboard", label: "Dashboard (tasks & events)" },
  { key: "financial", label: "Financials (costs, invoices, reports, money fields)" },
  { key: "generalAgenda", label: "General agenda" },
  { key: "generalChat", label: "General chat" },
];

const MODES = [
  { key: "your-cases", label: "Your cases" },
  { key: "rp-cases-only", label: "RP cases only" },
  { key: "firm-wide", label: "Firm-wide" },
];

type SurfaceScopesPanelProps = {
  toast?: (msg: string) => void;
  isAdmin?: boolean;
};

export default function SurfaceScopesPanel({ toast, isAdmin }: SurfaceScopesPanelProps) {
  const { t } = useTranslation();
  const [scopes, setScopes] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/firm-settings/surface-scopes")
      .then((s) => {
        if (!cancelled) setScopes(s && typeof s === "object" ? s : {});
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.message || "Failed to load surface scopes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isAdmin) return null;

  async function setMode(surface: string, mode: string) {
    if (scopes?.[surface] === mode) return;
    setBusyKey(surface);
    const prev = scopes;
    setScopes((s: any) => ({ ...s, [surface]: mode })); // optimistic
    try {
      const updated = await api("/firm-settings/surface-scopes", {
        method: "PUT",
        body: JSON.stringify({ [surface]: mode }),
      });
      setScopes(updated && typeof updated === "object" ? updated : { ...prev, [surface]: mode });
      toast &&
        toast(
          t("surfaceScopes.saved", { fallback: "Surface scope updated" }),
        );
    } catch (e) {
      setScopes(prev); // revert
      toast && toast("⚠ " + ((e as any)?.message || t("common.save_failed", { fallback: "Save failed" })));
    } finally {
      setBusyKey(null);
    }
  }

  if (loading)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
        {t("common.loading", { fallback: "Loading…" })}
      </p>
    );
  if (err)
    return <p style={{ fontSize: FS.s11, color: "var(--rd)" }}>{err}</p>;

  return (
    <div className="detail-card" style={{ padding: 12 }}>
      <p style={{ margin: "0 0 8px", fontSize: FS.s10_5, color: "var(--tm)" }}>
        {t("surfaceScopes.subtitle", {
          fallback:
            "Per-surface scope: which cases' data each surface shows. Admin always sees all cases regardless of these settings.",
        })}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {SURFACES.map((s) => {
          const cur = scopes?.[s.key];
          const busy = busyKey === s.key;
          return (
            <div
              key={s.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                opacity: busy ? 0.5 : 1,
                borderBottom: "1px solid rgba(255,255,255,.04)",
                paddingBottom: 6,
              }}
            >
              <span style={{ fontSize: FS.s11_5, fontWeight: 500 }}>
                {t(`surfaceScopes.surface.${s.key}`, { fallback: s.label })}
              </span>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                {MODES.map((m) => {
                  const on = cur === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      disabled={busy}
                      onClick={() => setMode(s.key, m.key)}
                      style={{
                        fontSize: FS.s9_5,
                        fontWeight: 700,
                        padding: "3px 9px",
                        borderRadius: RAD.r8,
                        background: on ? "rgba(74,124,255,.20)" : "transparent",
                        color: on ? "var(--ac)" : "var(--tm)",
                        border: `1px solid ${on ? "var(--ag)" : "var(--br)"}`,
                        cursor: busy ? "not-allowed" : "pointer",
                        fontFamily: "inherit",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(`surfaceScopes.mode.${m.key}`, { fallback: m.label })}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
